import { createSignal, For, JSX, Show, createResource } from "solid-js";
import { useTelescopeContext } from "@hubble/telescope";
import { HowToUseSteps } from "~/server/types/brand";
import { goBack } from "~/shared_states/modal";
import { clientRepo } from "~/server/apis/client_repo";

export type HowToUseModalProps = {
  brandId: string;
  headerText?: string;
};

export function HowToUseModal(props: HowToUseModalProps): JSX.Element {
  const { components } = useTelescopeContext();
  const [selectedTabIndex, setSelectedTabIndex] = createSignal<number>(0);

  // Fetch howToUseSteps from API
  const [howToUseStepsList] = createResource<HowToUseSteps[]>(async () => {
    const howToUseResponse = await clientRepo.getBrandHowToUseSteps(
      props.brandId
    );
    return howToUseResponse.howToUseSteps;
  });

  // Get current steps based on selected tab
  const currentSteps = () => {
    const steps = howToUseStepsList();
    if (!steps || steps.length === 0) return [];
    return steps[selectedTabIndex()]?.howToUseHints?.hints || [];
  };

  // Create segmented control items from howToUseSteps
  const segmentedItems = () => {
    const steps = howToUseStepsList();
    if (!steps) return [];
    return steps.map((step, index) => ({
      id: index.toString(),
      label: step.retailModeName,
    }));
  };

  return (
    <div class=" flex flex-col">
      {/* Modal Header */}
      <div class="sticky top-0">
        <components.ModalHeader
          size="small"
          title={props.headerText || "How to use"}
          onClose={() => {
            goBack();
          }}
        />
      </div>

      {/* Content */}
      <div class="flex w-full flex-1 flex-col items-start overflow-hidden">
        <Show
          when={howToUseStepsList.state === "ready"}
          fallback={
            <div class="flex h-[400px] w-full flex-1 items-center justify-center py-8">
              <components.Loader size="medium" />
            </div>
          }
        >
          {/* Segmented Control - Only show if there are multiple tabs */}
          <Show
            when={
              howToUseStepsList.latest && howToUseStepsList.latest.length > 1
            }
          >
            <div class="box-border flex w-full flex-col gap-[10px] px-5 pb-0 pt-3">
              <components.SegmentedControl
                items={segmentedItems()}
                activeItem={selectedTabIndex().toString()}
                onItemChange={(itemId) => setSelectedTabIndex(parseInt(itemId))}
                type="label-only"
                size="small"
                expanded={true}
              />
            </div>
          </Show>

          {/* Steps List */}
          <div class="box-border flex w-full flex-1 flex-col gap-3 overflow-y-auto px-5 py-5">
            <For each={currentSteps()}>
              {(step, index) => <StepItem step={step} number={index() + 1} />}
            </For>
          </div>
        </Show>
      </div>
    </div>
  );
}

/**
 * Individual Step Item
 */
function StepItem(props: { step: string; number: number }): JSX.Element {
  return (
    <div class="flex w-full items-start gap-3">
      {/* Serial Number Badge */}
      <div class="flex size-5 shrink-0 items-center justify-center rounded-full bg-background-normal-tertiary">
        <span class="text-label-medium text-text-normal-secondary">
          {props.number}
        </span>
      </div>

      {/* Step Text */}
      <div class="flex flex-1 flex-col justify-center">
        <p class="whitespace-pre-wrap text-title-6-regular leading-5 text-text-normal-primary">
          {props.step}
        </p>
      </div>
    </div>
  );
}
